/**
 * Sends a signed-in user who belongs to no workspace to
 * /welcome/request-access. New users get no workspace automatically
 * (workspace isolation, phase 1); an admin has to add them.
 *
 * Installation admins are never redirected — they act in every workspace.
 * /welcome, /auth and /settings stay reachable (the local password change
 * lives in Settings).
 */
import { useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useActiveWorkspace } from './ActiveWorkspaceContext';

const BYPASS_PREFIXES = ['/welcome', '/auth', '/settings'];

function bypass(pathname: string): boolean {
  return BYPASS_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

export function WorkspaceAccessRedirector({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { workspaces, loading } = useActiveWorkspace();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading || !user || user.role === 'admin') return;
    if (workspaces.length > 0 || bypass(location.pathname)) return;
    navigate('/welcome/request-access', { replace: true });
  }, [loading, user, workspaces.length, location.pathname, navigate]);

  return <>{children}</>;
}
